/**
 * Gestion de usuarios. Solo la ve un ADMIN.
 *
 * ===========================================================================
 * LO QUE ESTA PANTALLA HACE, Y LO QUE NO
 *
 * Crear, dar de baja, reactivar y resetear la contrasena. No hay borrado: un
 * usuario firma rate_sheets y es dueño de cotizaciones, y borrarlo dejaria
 * documentos sin autor [services/api/src/usuarios.ts]. "Dar de baja" es lo que
 * se quiere decir al decir "borra a fulano": deja de entrar, y lo que hizo
 * sigue siendo suyo.
 *
 * Tampoco hay edicion de nombre, correo, rol ni `ve_costo`. No es un olvido:
 * cambiar `ve_costo` a alguien decide si ve los costos del proveedor
 * [CLAUDE.md, Visibilidad], y mientras eso no tenga una pantalla propia --con
 * su confirmacion y su rastro de quien lo cambio-- es mejor que no exista un
 * desplegable suelto en una fila de tabla. Se da de baja y se crea de nuevo.
 *
 * ===========================================================================
 * TRES COLUMNAS QUE PARECEN DECORATIVAS Y NO LO SON
 *
 *   ve_costo            explica por que dos agentes ven pantallas distintas.
 *                       Es la pregunta que llega por telefono.
 *
 *   contrasena          `false` significa "no puede entrar por la via propia":
 *                       el ADMIN sembrado antes de correr `npm run contrasena`,
 *                       y quien algun dia entre solo por Entra ID. NO significa
 *                       que cualquier contrasena sirva [migracion 0020].
 *
 *   sesiones abiertas   es lo que hace visible que una baja o un reseteo
 *                       echaron a alguien de verdad y ahora, y no cuando
 *                       caduque algo.
 * ===========================================================================
 */

import { el } from '../dom';
import { formatearFecha } from '../formato';
import type { RolUsuario, UsuarioAdmin } from '../api';

/** Los cuatro roles, en el orden del enum. Es una lista de CODIGOS estables:
 *  su etiqueta sale de `usuarios.rol.*` en el diccionario. */
const ROLES: readonly RolUsuario[] = ['AGENTE', 'SUPERVISOR', 'OPERADOR', 'ADMIN'];
import type { Idioma, Traductor } from '../i18n';

/**
 * Espejo de MINIMO en services/api/src/contrasena.ts. Duplicado y marcado como
 * tal: el navegador no puede importar del servicio. Sirve para el `minlength`,
 * que evita el viaje; si divergen manda la API.
 */
export const MIN_CONTRASENA = 12;

export interface EstadoAltaUsuario {
  readonly nombre: string;
  readonly email: string;
  readonly rol: RolUsuario;
  /** `null` y no `false`: es una eleccion que hay que hacer, no un valor por
   *  defecto. La API tambien la exige explicita. */
  readonly veCosto: boolean | null;
  readonly contrasena: string;
}

export const ALTA_VACIA: EstadoAltaUsuario = {
  nombre: '',
  email: '',
  rol: 'AGENTE',
  veCosto: null,
  contrasena: '',
};

export interface OpcionesUsuarios {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly usuarios: readonly UsuarioAdmin[];
  readonly cargando: boolean;
  readonly trabajando: boolean;
  /** Quien esta mirando. Su fila no lleva boton de baja: la API lo rechaza y
   *  enseñar un boton que siempre falla es peor que no tenerlo. */
  readonly yo: string;
  readonly alta: EstadoAltaUsuario | null;
  readonly reseteando: { readonly id: string; readonly contrasena: string } | null;
  readonly aviso: string | null;
  readonly onAbrirAlta: (abierta: boolean) => void;
  /** Abre el alta de VARIOS. Es la via normal cuando entra un equipo: las
   *  contrasenas las genera el servidor y nadie improvisa diez [lote-usuarios.ts]. */
  readonly onAbrirLote: () => void;
  readonly onCambioAlta: (v: EstadoAltaUsuario) => void;
  readonly onCrear: () => void;
  readonly onEstado: (id: string, activo: boolean) => void;
  readonly onAbrirReseteo: (id: string | null) => void;
  readonly onCambioReseteo: (contrasena: string) => void;
  readonly onResetear: () => void;
  /**
   * Salir de la lista.
   *
   * Opcional desde que hay menu lateral: esta lista es la raiz de su zona, asi
   * que quien quiera irse usa el menu. Un "Volver" que salta a otra zona
   * distinta es justo el control sin destino claro que despista a quien acaba
   * de llegar. Se conserva para cuando la lista se abra desde otro sitio.
   */
  readonly onVolver?: () => void;
}

function campo(
  id: string,
  etiqueta: string,
  control: HTMLElement,
  ayuda?: string,
): HTMLElement {
  return el('div', { clase: 'campo' }, [
    el('label', { clase: 'etiqueta', texto: etiqueta, attrs: { for: id } }),
    control,
    ayuda === undefined ? null : el('p', { clase: 'ayuda tenue', texto: ayuda }),
  ]);
}

function entrada(
  id: string,
  tipo: string,
  valor: string,
  onCambio: (v: string) => void,
  extra: Record<string, string> = {},
): HTMLInputElement {
  const nodo = el('input', {
    clase: 'control',
    attrs: { id, type: tipo, value: valor, ...extra },
  });
  nodo.addEventListener('input', () => {
    onCambio(nodo.value);
  });
  return nodo;
}

function formularioAlta(o: OpcionesUsuarios): HTMLElement {
  const { t } = o;
  const a = o.alta!;

  const rol = el('select', { clase: 'control', attrs: { id: 'usuario-rol' } });
  // El orden es el del enum de la base [migracion 0029]: de menos alcance a
  // mas. La etiqueta se traduce; lo que se manda es el CODIGO, siempre
  // [CLAUDE.md, Idioma].
  for (const r of ROLES) {
    rol.appendChild(el('option', { texto: t(`usuarios.rol.${r}`), attrs: { value: r } }));
  }
  rol.value = a.rol;

  // Que hace el rol elegido, en una linea, debajo del desplegable. Cuatro
  // nombres --Agente, Supervisor, Operador, Administrador-- no dicen cual firma
  // tarifas y cual no, y quien da de alta a diez personas en una tarde no
  // deberia tener que acordarse. Se actualiza a mano y no repintando el
  // formulario: un repintado en cada cambio se llevaria el foco.
  const detalleRol = el('p', {
    clase: 'ayuda tenue',
    texto: t(`usuarios.rolDetalle.${a.rol}`),
  });

  rol.addEventListener('change', () => {
    const elegido = rol.value as RolUsuario;
    detalleRol.textContent = t(`usuarios.rolDetalle.${elegido}`);
    o.onCambioAlta({ ...a, rol: elegido });
  });

  const veCosto = el('select', { clase: 'control', attrs: { id: 'usuario-ve-costo' } });
  veCosto.appendChild(el('option', { texto: t('usuarios.elige'), attrs: { value: '' } }));
  veCosto.appendChild(
    el('option', { texto: t('usuarios.veCosto.si'), attrs: { value: 'si' } }),
  );
  veCosto.appendChild(
    el('option', { texto: t('usuarios.veCosto.no'), attrs: { value: 'no' } }),
  );
  veCosto.value = a.veCosto === null ? '' : a.veCosto ? 'si' : 'no';
  veCosto.addEventListener('change', () => {
    o.onCambioAlta({
      ...a,
      veCosto: veCosto.value === '' ? null : veCosto.value === 'si',
    });
  });

  const form = el('form', { clase: 'usuarios-alta' }, [
    campo(
      'usuario-nombre',
      t('usuarios.campo.nombre'),
      entrada(
        'usuario-nombre',
        'text',
        a.nombre,
        (v) => {
          o.onCambioAlta({ ...a, nombre: v });
        },
        { required: 'required', maxlength: '200' },
      ),
    ),
    campo(
      'usuario-email',
      t('usuarios.campo.email'),
      entrada(
        'usuario-email',
        'email',
        a.email,
        (v) => {
          o.onCambioAlta({ ...a, email: v });
        },
        { required: 'required', autocapitalize: 'none', spellcheck: 'false' },
      ),
    ),
    campo(
      'usuario-rol',
      t('usuarios.campo.rol'),
      el('div', {}, [rol, detalleRol]),
      t('usuarios.ayuda.rol'),
    ),
    campo(
      'usuario-ve-costo',
      t('usuarios.campo.veCosto'),
      veCosto,
      t('usuarios.ayuda.veCosto'),
    ),
    campo(
      'usuario-contrasena',
      t('usuarios.campo.contrasena'),
      entrada(
        'usuario-contrasena',
        'text',
        a.contrasena,
        (v) => {
          o.onCambioAlta({ ...a, contrasena: v });
        },
        // Ya NO es obligatoria: vacia significa "generala tu", que es lo que
        // deberia hacerse siempre. Se conserva el campo para el caso en que
        // alguien tenga que dictar una concreta.
        { minlength: String(MIN_CONTRASENA), autocomplete: 'off', placeholder: '' },
      ),
      // En claro y no oculta: quien la escribe tiene que dictarsela a la
      // persona. Mientras no haya envio de correo, un ADMIN conoce la
      // contrasena inicial de cada uno --por eso quien entra con ella tiene que
      // cambiarla antes de llegar a nada [migracion 0032]--.
      t('usuarios.ayuda.contrasena', { n: String(MIN_CONTRASENA) }),
    ),
    el('div', { clase: 'rfq-acciones' }, [
      el('button', {
        clase: 'boton boton-primario',
        texto: t('usuarios.crear'),
        attrs: o.trabajando
          ? { type: 'submit', disabled: 'disabled' }
          : { type: 'submit' },
      }),
      el('button', {
        clase: 'boton boton-plano',
        texto: t('usuarios.cancelar'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onAbrirAlta(false);
        },
      }),
    ]),
  ]);

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (!o.trabajando) o.onCrear();
  });

  return el('section', { clase: 'panel usuarios-panel-alta' }, [
    el('h3', { texto: t('usuarios.alta.titulo') }),
    form,
  ]);
}

function filaReseteo(o: OpcionesUsuarios, u: UsuarioAdmin): HTMLElement {
  const { t } = o;
  const r = o.reseteando!;

  const form = el('form', { clase: 'usuarios-reseteo' }, [
    el('label', {
      clase: 'etiqueta',
      texto: t('usuarios.reseteo.etiqueta', { nombre: u.nombre }),
      attrs: { for: 'usuario-nueva' },
    }),
    // Sin `required`: vacia significa "generala tu", y el servidor devuelve la
    // que genero una sola vez. Es la via preferible --nadie improvisa una
    // contrasena-- y por eso la ayuda lo dice.
    entrada('usuario-nueva', 'text', r.contrasena, o.onCambioReseteo, {
      minlength: String(MIN_CONTRASENA),
      autocomplete: 'off',
    }),
    el('div', { clase: 'rfq-acciones' }, [
      el('button', {
        clase: 'boton boton-primario boton-menor',
        texto: t('usuarios.reseteo.confirmar'),
        attrs: o.trabajando
          ? { type: 'submit', disabled: 'disabled' }
          : { type: 'submit' },
      }),
      el('button', {
        clase: 'boton boton-plano boton-menor',
        texto: t('usuarios.cancelar'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onAbrirReseteo(null);
        },
      }),
    ]),
    // Lo que va a pasar, dicho antes de pulsar: el reseteo echa a quien
    // estuviera dentro con la anterior, y eso no se deshace.
    el('p', { clase: 'ayuda tenue', texto: t('usuarios.reseteo.ayuda') }),
  ]);

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (!o.trabajando) o.onResetear();
  });

  return el('tr', { clase: 'usuarios-fila-reseteo' }, [
    el('td', { attrs: { colspan: '7' } }, [form]),
  ]);
}

export function pintarUsuarios(o: OpcionesUsuarios): HTMLElement {
  const { t } = o;

  const filas: HTMLElement[] = [];
  for (const u of o.usuarios) {
    filas.push(
      el('tr', { clase: u.activo ? '' : 'usuarios-baja' }, [
        el('td', {}, [
          el('strong', { texto: u.nombre }),
          u.id === o.yo
            ? el('span', { clase: 'badge usuarios-tu', texto: t('usuarios.tu') })
            : null,
        ]),
        // El correo es la identidad: se pinta tal cual, sin recortar.
        el('td', { clase: 'usuarios-email', texto: u.email }),
        el('td', { texto: t(`usuarios.rol.${u.rol}`) }),
        // Texto y no un icono: un badge que solo se distingue por el color no
        // existe para quien no distingue esos dos colores.
        el('td', {
          texto: u.ve_costo ? t('usuarios.veCosto.si') : t('usuarios.veCosto.no'),
        }),
        el('td', { clase: 'usuarios-estado' }, [
          el('span', {
            // Ni verde ni rojo [estilo/tokens.css]: el verde es dinero a
            // nuestro favor y el rojo es alerta. Un usuario de baja no es
            // ninguna de las dos, es una decision tomada.
            clase: `badge ${u.activo ? 'badge-primario' : ''}`,
            texto: u.activo ? t('usuarios.activo') : t('usuarios.inactivo'),
          }),
          u.tiene_contrasena
            ? null
            : el('span', {
                clase: 'badge badge-atencion',
                texto: t('usuarios.sinClave'),
              }),
          // Todavia no ha elegido la suya: la que tiene se la dictaron, y la
          // sabe quien se la dio. Se marca porque es una cuenta a medio abrir,
          // y saber cuales quedan es lo que dice a quien hay que recordarselo.
          u.debe_cambiar_contrasena
            ? el('span', {
                clase: 'badge badge-atencion',
                texto: t('usuarios.claveTemporal'),
                attrs: { title: t('usuarios.claveTemporalAyuda') },
              })
            : null,
        ]),
        // DOS DATOS DISTINTOS EN UNA CELDA, CADA UNO CON SU NOMBRE.
        //
        // Antes eran un numero y una fecha apilados, bajo una cabecera con
        // barra --"Sesiones / contraseña"--, y habia que deducir cual era cual.
        // Peor con cero sesiones: un cero y una fecha debajo se leen como si la
        // fecha fuera del cero.
        //
        // La cabecera se queda corta porque es una columna; el nombre de cada
        // valor va en la celda, que es donde se lee el valor.
        el('td', { clase: 'usuarios-sesiones' }, [
          el('span', {
            texto: t('usuarios.nSesiones', { n: String(u.sesiones_abiertas) }),
          }),
          u.contrasena_actualizada_en === null
            ? // [H59] Nunca la cambio nadie. Se dice: un hueco aqui parece un
              // dato que no cargo.
              el('span', {
                clase: 'tenue usuarios-fecha',
                texto: t('usuarios.contrasenaSinFecha'),
              })
            : el('span', {
                clase: 'tenue usuarios-fecha',
                texto: t('usuarios.contrasenaDesde', {
                  fecha: formatearFecha(u.contrasena_actualizada_en, o.idioma),
                }),
              }),
        ]),
        el('td', { clase: 'usuarios-acciones' }, [
          el('button', {
            clase: 'boton boton-plano boton-menor',
            texto: t('usuarios.reseteo.abrir'),
            attrs: { type: 'button' },
            onClick: () => {
              o.onAbrirReseteo(u.id);
            },
          }),
          // La fila de quien mira no lleva baja: la API la rechaza --nadie se
          // da de baja a si mismo-- y un boton que siempre falla es peor que su
          // ausencia.
          u.id === o.yo
            ? null
            : el('button', {
                clase: 'boton boton-menor',
                texto: u.activo ? t('usuarios.darBaja') : t('usuarios.reactivar'),
                attrs: o.trabajando
                  ? { type: 'button', disabled: 'disabled' }
                  : { type: 'button' },
                onClick: () => {
                  o.onEstado(u.id, !u.activo);
                },
              }),
        ]),
      ]),
    );

    if (o.reseteando?.id === u.id) filas.push(filaReseteo(o, u));
  }

  return el('div', { clase: 'usuarios' }, [
    el('section', { clase: 'panel panel-rfq-lista' }, [
      el('div', { clase: 'rfq-cabecera' }, [
        el('h2', { texto: t('usuarios.titulo') }),
        el('div', { clase: 'rfq-acciones' }, [
          el('button', {
            clase: 'boton boton-primario',
            texto: t('usuarios.nuevo'),
            attrs: { type: 'button' },
            onClick: () => {
              o.onAbrirAlta(o.alta === null);
            },
          }),
          // Primero el lote y no el alta suelta: es lo que se hace cuando entra
          // un equipo, y es la unica de las dos en la que nadie tiene que
          // inventarse una contrasena.
          el('button', {
            clase: 'boton boton-primario',
            texto: t('lote.abrir'),
            attrs: { type: 'button' },
            onClick: o.onAbrirLote,
          }),
          o.onVolver === undefined
            ? null
            : el('button', {
                clase: 'boton boton-plano',
                texto: t('rfq.volver'),
                attrs: { type: 'button' },
                onClick: o.onVolver,
              }),
        ]),
      ]),

      o.aviso === null ? null : el('p', { clase: 'aviso aviso-neutro', texto: o.aviso }),
      o.cargando ? el('p', { clase: 'tenue', texto: t('usuarios.cargando') }) : null,

      o.usuarios.length === 0 && !o.cargando
        ? el('p', { clase: 'tenue', texto: t('usuarios.vacia') })
        : el('table', { clase: 'tabla-solicitudes tabla-usuarios' }, [
            el('thead', {}, [
              el('tr', {}, [
                el('th', { texto: t('usuarios.col.nombre') }),
                el('th', { texto: t('usuarios.col.email') }),
                el('th', { texto: t('usuarios.col.rol') }),
                el('th', { texto: t('usuarios.col.veCosto') }),
                el('th', { texto: t('usuarios.col.estado') }),
                el('th', { texto: t('usuarios.col.sesiones') }),
                el('th', { texto: t('usuarios.col.acciones') }),
              ]),
            ]),
            el('tbody', {}, filas),
          ]),
    ]),

    o.alta === null ? null : formularioAlta(o),
  ]);
}
